import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMerchantContext from '../../hooks/useMerchantContext';
import useStampCardContext from '../../hooks/useStampCardContext';
import useNavContext from '../../hooks/useNavContext';

import * as MerchantService from '../../api/merchantService';
import * as StampCardService from '../../api/stampCardService';

const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 1,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  toolbar: {
    height: 40,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  box: {
    flex: 1,
    display: 'flex',
  },
  close: {
    marginRight: 'auto',
  },
  actions: {
    marginLeft: 'auto',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(4),
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  selectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    textAlign: 'left',
  },
  unselectedMerchantText: {
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'left',
  },
  homeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(),
  },
  homeIcon: {
    marginRight: theme.spacing(1),
    fontSize: 30,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 1)',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(4),
  },

  listItemContainer: {
    marginLeft: 0,
  },
  container: {
    paddingBottom: theme.spacing(12),
  },
}));

export default function Programs() {
  const classes = useStyles();
  const history = useHistory();
  const nav = useNavContext();
  const selectedMerchant = useMerchantContext();
  const selectedStampCard = useStampCardContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [merchantList, setMerchantList] = useState<MerchantService.Merchant[]>(
    [],
  );

  useEffect(() => {
    const fetch = async () => {
      const resMerchantList = await MerchantService.listMerchantsByMembership();
      const resStampCardList = await StampCardService.list();
      if (
        resMerchantList instanceof Error ||
        resStampCardList instanceof Error
      ) {
        setIsLoading(false);
        return;
      }
      setMerchantList(resMerchantList);
      setIsLoading(false);
    };

    setMerchantList(merchantList);

    if (isLoading) {
      fetch();
    }
  }, [merchantList, isLoading]);

  const handleMerchantClick = async (merchantId: string) => {
    console.log('Selected merchant ID: ', merchantId);

    selectedMerchant?.setMerchant({
      merchantId: merchantId,
    });

    const resStampCardListByMerchant =
      await StampCardService.listByMerchantId(merchantId);
    if (resStampCardListByMerchant instanceof Error) {
      selectedStampCard?.setStampCard({
        selectedStampCardId: '',
        selectedSlotNumber: 0,
        selectedStampCampaignId: '0',
      });
      history.push(`/merchant`);
      nav?.setNav({
        menuNav: 'home',
      });
      return;
    }

    if (resStampCardListByMerchant.length > 0) {
      selectedStampCard?.setStampCard({
        selectedStampCardId: resStampCardListByMerchant[0].id,
        selectedSlotNumber: resStampCardListByMerchant[0].slotNumber,
        selectedStampCampaignId:
          resStampCardListByMerchant[0].loyalty_campaign.id,
      });
      history.push(`/merchant`);
      nav?.setNav({
        menuNav: 'home',
      });
      return;
    } else {
      selectedStampCard?.setStampCard({
        selectedStampCardId: '',
        selectedSlotNumber: 0,
        selectedStampCampaignId: '0',
      });
      history.push(`/merchant`);
      nav?.setNav({
        menuNav: 'home',
      });
      return;
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <div className={classes.homeContainer}>
          <Typography variant="h4" className={classes.title}>
            Your Programs
          </Typography>
        </div>
      </div>
      <>
        <List>
          {merchantList.map((merchant) => (
            <ListItem
              button
              key={merchant.id}
              onClick={() => {
                handleMerchantClick(merchant.id);
              }}
              className={classes.listItemContainer}
            >
              <div className={classes.contentContainer}>
                <Avatar
                  alt={merchant.name}
                  src={merchant.logo?.versions.xl}
                  className={classes.avatar}
                />
                <Typography
                  variant="h5"
                  className={
                    selectedMerchant?.merchant?.merchantId === merchant.id
                      ? classes.selectedMerchantText
                      : classes.unselectedMerchantText
                  }
                  gutterBottom
                >
                  {merchant.name}
                </Typography>
              </div>
            </ListItem>
          ))}
        </List>
      </>
    </div>
  );
}
